<template>
  <div class="app-body landing-body"
       :class="[isSignup ? 'page-signup' : 'page-signin',
                {'page-account-exists': accountExists},
                {'account-fybn': accountFirebaseYesBackendNo}]">
    <Header>
    </Header>
    <main class="container">
      <div class="row justify-content-center">
        <div class="col-md-6">
          <div class="card">
            <div class="card-header title-sign-up" :class="[isSignup ? 'd-block' : 'd-none']">
              <h2 class="title">Create GanttProject Cloud Account</h2>
              <div>
                Are you already registered here? <a :href="routes.LOGIN" class="link">Sign in</a> to your account.
              </div>
            </div>
            <div class="card-header title-sign-in" :class="[isSignup ? 'd-none' : 'd-block']">
              <h2 class="title">Sign In to GanttProject Cloud</h2>
              <div>
                New to GanttProject cloud? <a :href="routes.SIGNUP" class="link">Create an account</a>
              </div>
            </div>
            <div class="card-body">
              <div id="firebaseui-auth-container"></div>
              <!-- Implements 2.2.2 -->
              <div class="no-account">
                <h3>You must be new here!</h3>
                <p class="help-text mb-5">No account found for {{ signedInEmail || 'this email address' }}.</p>
                <a :href="routes.SIGNUP" class="btn btn-attention w-100">Sign Up</a>
                <a :href="routes.LOGIN" class="btn btn-outline-secondary mt-3 w-100" @click.prevent="signOutAndSignin">Sign in with a different account</a>
              </div>
              <!-- Implements 3.2.1 -->
              <div class="account-exists text-left">
                <p class="alert alert-warning">An account associated with {{ signedInEmail || 'this email address' }} already exists.</p>
                <p class="m-5">Would you like to sign in with this account or start over with another email address?</p>
                <a href="#" class="btn btn-attention w-100" @click.prevent="redirectToPostAuth">Continue and sign in</a>
                <a href="#" class="btn btn-outline-secondary mt-3 w-100" @click.prevent="signOutAndSignup">Try another account</a>
              </div>
              <div id="loader"><i class="fa fa-spin fa-spinner"></i>&nbsp;Loading&hellip;</div>
              <div class="help-text mt-3 text-center">
                We do not store your passwords and do not sell your personal information.
                <br>
                <a :href="routes.ABOUT_TOS" class="link" target="_blank">Terms of Service</a>&nbsp;|&nbsp;
                <a :href="routes.ABOUT_PRIVACY" class="link" target="_blank">Privacy Policy</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    <ErrorWidget ref="errorWidget"></ErrorWidget>
  </div>
</template>

<script lang="ts">
import {Vue, Component, Ref} from 'vue-facing-decorator';
import ErrorWidget from "@/components/ErrorWidget.vue";
import Header from "@/components/Header.vue";
import {installGoogleAnalytics} from "@/utils/Analytics";
import * as Auth from "@/auth/Auth";
import {AboutPrivacy, AboutTos, PostAuth, SetRedirect, Signup} from "@/Routes";
import {Signin as SigninEndpoint} from "@/Routes";
import {backendAccountExists} from "@/Postauth";
import {showUi} from "@/Signup";
import {User} from "@/auth/Auth";
import {isXhrError, setErrorWidget, showError} from "@/utils/Ui";
import {ERR_AUTH_PROCESS, userGetErrorMessage} from "@/utils/ErrorMessages";

@Component({
  components: {Header, ErrorWidget}
})
export default class Signin extends Vue {
  private routes = {
    ABOUT_TOS: AboutTos.url,
    ABOUT_PRIVACY: AboutPrivacy.url,
    // A load without these arguments wipes what sendSigninCheckin remembered, so jumping between the
    // two pages has to carry them over.
    LOGIN: SigninEndpoint.url + window.location.search,
    SIGNUP: Signup.url + window.location.search,
  }
  // Compared against the sign-in page on purpose: an unexpected path leaves sign-up working,
  // while the other way round it would turn disableSignUp on and leave a new user nowhere to go.
  private isSignup = window.location.pathname !== SigninEndpoint.url;
  private accountExists = false;
  private accountFirebaseYesBackendNo = false;
  private signedInEmail = '';
  @Ref
  private errorWidget: ErrorWidget;

  public mounted() {
    installGoogleAnalytics();
    setErrorWidget(this.errorWidget);
    this.sendSigninCheckin().always( () =>{
      Auth.init().then(this.onUserReady).catch((err) => {
        console.error(err);
        console.log("User is not authenticated. Showing the auth UI");
        this.showAuthUi();
      });
    })
  }

  private onUserReady(user: User | undefined): PromiseLike<any> {
    if (!user) {
      return Promise.reject(Error("User is not authenticated"));
    }
    console.log("User is authenticated.");
    // A Firebase account is not an account with us: a federated provider creates one for whoever signs
    // in, and an abandoned sign-up leaves one behind too.
    return Promise.resolve(backendAccountExists(user)).then(
        (exists) => {
          if (!exists && !this.isSignup) {
            console.log("There is a Firebase account but not the backend account, and we are on signin");
            if (user.isEmailAuth()) {
              // Post-auth will either show a consent screen, or will show "verify your email" interstitial
              // or will remind user to check their email.
              this.redirectToPostAuth();
              return;
            }

            this.signedInEmail = user.email() ?? '';
            this.accountFirebaseYesBackendNo = true;
            return;
          }
          if (exists && this.isSignup) {
            console.log("There is a Firebase account and the backend account, but we are on signup");
            this.showAccountMismatch(user);
            return;
          }
          // We are here if
          // 1) it is signin and the backend account exists (scenario 3.1.1) => it is a success path, just redirect to postauth
          // 2) it is signup and there is no backend account (scenario 3.2.2 and 3.2.3) =>
          //    it is a success path (3.2.2) for Google, or maybe 3.2.3 for email => /postauth will do the work.
          console.log("Sending post-auth redirect");
          this.redirectToPostAuth();
        },
        (err) => {
          console.error(err);
          // Post-auth explains a soft-deleted account properly. On any other failure it would only
          // repeat this request, so back to the form instead.
          if (isXhrError(err) && err.status === 403) {
            this.redirectToPostAuth();
            return;
          }
          // No sign-out here: the message asks for a reload, and a reload is what retries this
          // request. Dropping the session would turn a blip into a full re-authentication.
          showError(isXhrError(err) ? userGetErrorMessage(err.status) : ERR_AUTH_PROCESS);
          this.showAuthUi();
        },
    );
  }

  /** Implements 3.2.1: on /signup a user who already has an account is offered Continue | Sign Out. */
  private showAccountMismatch(user: User) {
    // A federated sign-in types no address anywhere, so the message names the one it brought.
    this.signedInEmail = user.email() ?? '';
    this.accountExists = true;
  }

  private signOutAndSignup() {
    Auth.signOut(false).then(() => window.location.assign(this.routes.SIGNUP));
  }

  private signOutAndSignin() {
    Auth.signOut(false).then(() => window.location.assign(this.routes.LOGIN));
  }

  /**
   * Only /signin has a stranger to recognise (3.1.4), while from /signup an unknown
   * user is one being created (3.2.4).
   */
  private redirectToPostAuth() {
    PostAuth.redirect(this.isSignup ? undefined : {from: 'signin'});
  }

  private showAuthUi() {
    showUi(this.isSignup, () => this.onUserReady(Auth.getUser()).then(undefined, console.error));
  }

  /** Lets the server remember the redirect and callback port arguments in the session. */
  private sendSigninCheckin(): JQueryXHR {
    const queryArgs = new URLSearchParams(window.location.search);
    const redirectUrl = queryArgs.get('redirect') || queryArgs.get('redirectUrl');
    const callbackPort = queryArgs.get('callback');
    const fallback = queryArgs.get('fallback');
    const checkinArgs = {
      redirect: redirectUrl,
      callback: callbackPort ? parseInt(callbackPort, 10) : undefined,
      fallback: fallback
    }
    return SetRedirect.invoke(checkinArgs);
  }
}

</script>

<style lang="scss">
@import "sass/login";
@import '~/firebaseui/dist/firebaseui.css';
.page-signin, .page-signup {
  #firebaseui-auth-container .firebaseui-container {
    box-shadow: none;
  }

  .firebaseui-title {
    border-bottom: none;
  }

  // These titles repeat the card header. Cards left out keep theirs because it says something the
  // header does not: "You already have an account", in particular.
  .firebaseui-id-page-provider-sign-in .firebaseui-title,
  .firebaseui-id-page-sign-in .firebaseui-title,
  .firebaseui-id-page-password-sign-up .firebaseui-title {
    display: none;
  }

  // The bundle puts it at a hardcoded 55px, the room a title takes; without one it crosses a field.
  .mdl-progress.firebaseui-busy-indicator {
    top: 0;
  }

  .no-account, .account-exists {
    display: none;
    text-align: center;
  }
}

// Only the sign-in page hides this one outright; the sign-up page replaces it, in the last rule here.
.page-signin .firebaseui-id-page-password-sign-in .firebaseui-title {
  display: none;
}

.page-signup.page-account-exists {
  #firebaseui-auth-container, #loader {
    display: none;
  }

  .account-exists {
    display: block;
  }
}

// This applies in 2.2.2 case when user authenticates with email.
// In this case Firebase UI display its own "unauthorized user" message which we hide and
// show our own "you must be new" block.
.page-signin #firebaseui-auth-container:has(.firebaseui-id-page-unauthorized-user) {
  display: none;

  ~ .no-account {
    display: flex;
    flex-direction: column;
  }
}

// This is shown in 2.2.2 case when user authenticates with Google.
.page-signin.account-fybn {
  // Hide Firebase remnants
  #firebaseui-auth-container, #loader {
    display: none;
  }
  // Show "you must be new" block.
  .no-account {
    display: flex;
    flex-direction: column;
  }
}

// This applies in 3.2.* cases when user authenticates with email on signup page, and there is an
// existing Firebase account.
// In this case Firebase UI shows "Sign In" button. We also toggle our title to "Sign in" to stay consistent.
// We will branch to 3.2.1 or other cases later.
.page-signup:not(.page-account-exists) .card:has(.firebaseui-id-page-password-sign-in) {
  .title-sign-up {
    display: none !important;
  }
  .title-sign-in {
    display: block !important;
  }
}


</style>
